import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { CountdownOverlay } from '../components/CountdownOverlay';
import { DezoomDots } from '../components/DezoomDots';
import { GuessForm } from '../components/GuessForm';
import { PlayZoomStage } from '../components/PlayZoomStage';
import { ZoomImage } from '../components/ZoomImage';
import type { PhotoAttributionData } from '../components/PhotoAttribution';
import { useGameCountdown } from '../hooks/useGameCountdown';
import { useAuth } from '../hooks/useAuth';
import { t } from '../hooks/useT';
import { preloadImages } from '../utils/imageCache';

interface StartResponse {
  runId: string;
  startedAt?: string;
  puzzleIndex: number;
  puzzle: { puzzleNumber: number; attribution?: PhotoAttributionData | null };
  zoomLevels: { level: number; imageUrl: string }[];
  currentZoomLevel: number;
  resumed?: boolean;
}

interface GuessResponse {
  correct: boolean;
  status: string;
  dezoomsUsed: number;
  currentZoomLevel: number;
  score: number | null;
}

export function LeaguePlayPage() {
  const { id, puzzleIndex } = useParams<{ id: string; puzzleIndex: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [runId, setRunId] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(0);
  const [zoomLevels, setZoomLevels] = useState<{ level: number; imageUrl: string }[]>([]);
  const [dezooms, setDezooms] = useState(0);
  const [attribution, setAttribution] = useState<PhotoAttributionData | null>(null);
  const [puzzleNum, setPuzzleNum] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [finished, setFinished] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [timerSec, setTimerSec] = useState(0);
  const [skipCountdown, setSkipCountdown] = useState(false);
  const [countdownEnabled, setCountdownEnabled] = useState(false);
  const [ready, setReady] = useState(false);

  const init = useCallback(async () => {
    if (!id || !puzzleIndex) return;
    const data = await api<StartResponse>(
      `/leagues/${id}/today/${puzzleIndex}/start`,
      { method: 'POST' },
    );
    setRunId(data.runId);
    setZoomLevels(data.zoomLevels);
    setZoomLevel(data.currentZoomLevel ?? 0);
    setPuzzleNum(data.puzzle.puzzleNumber);
    setAttribution(data.puzzle.attribution ?? null);
    preloadImages(data.zoomLevels.map((z) => z.imageUrl));
    if (data.startedAt && data.resumed) setStartedAt(new Date(data.startedAt).getTime());
    setSkipCountdown(!!data.resumed);
    setCountdownEnabled(true);
    setReady(true);
  }, [id, puzzleIndex]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (!user.isPremium) {
      navigate('/premium', { replace: true });
      return;
    }
    init().catch((e) => setError(e.message));
  }, [authLoading, user, navigate, init]);

  const onGo = useCallback(async () => {
    if (!runId) return;
    const res = await api<{ startedAt: string }>(`/runs/${runId}/begin`, { method: 'POST' });
    setStartedAt(new Date(res.startedAt).getTime());
    setTimerSec(0);
  }, [runId]);

  const { display, revealed, ready: countdownReady } = useGameCountdown({
    enabled: countdownEnabled && !finished && ready,
    skip: skipCountdown,
    onGo,
  });

  useEffect(() => {
    if (!startedAt) return;
    const tid = setInterval(() => {
      setTimerSec(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(tid);
  }, [startedAt]);

  const handleGuess = async (guess: string): Promise<boolean | void> => {
    if (!id || !runId) return;
    setBusy(true);
    try {
      const res = await api<GuessResponse>(
        `/leagues/${id}/runs/${runId}/guess`,
        { method: 'POST', body: JSON.stringify({ guess }) },
      );
      setDezooms(res.dezoomsUsed);
      setZoomLevel(res.currentZoomLevel);
      if (res.status !== 'in_progress') {
        setFinished(true);
        setScore(res.score ?? 0);
        setCountdownEnabled(false);
      }
      return res.correct;
    } catch (e: unknown) {
      const err = e as { data?: { error?: string } };
      setError(err.data?.error || t('common.error'));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const formatTimer = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (!ready && !error) {
    return <main className="page play"><p className="loading">{t('common.loading')}</p></main>;
  }

  if (finished) {
    return (
      <main className="page arena-result">
        <div className="profile-card">
          <h1>{score && score > 0 ? t('result.win') : t('result.fail')}</h1>
          {score != null && score > 0 && <p className="score">{score} pts</p>}
          <Link to={`/leagues/${id}`} className="btn primary block">{t('leagues.back')}</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="page play">
      <header className="play-header">
        <div className="play-header-left">
          <span className="puzzle-num practice-badge">{t('leagues.title')} #{puzzleNum}</span>
          {startedAt != null && <span className="play-timer">{formatTimer(timerSec)}</span>}
        </div>
        <DezoomDots used={dezooms} />
      </header>
      <PlayZoomStage revealed={revealed} countdown={<CountdownOverlay value={display} />}>
        <ZoomImage levels={zoomLevels} activeLevel={zoomLevel} attribution={attribution} />
      </PlayZoomStage>
      <footer className="play-footer">
        {error && <p className="play-error" role="alert">{error}</p>}
        <GuessForm onSubmit={handleGuess} disabled={busy || !countdownReady || !runId} />
      </footer>
    </main>
  );
}
